import { collection, query, where, getDocs, limit, orderBy, and, or, getFirestore } from "firebase/firestore";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { SEARCH_CONFIG, MAP_CONFIG, DEBOUNCE_DELAY } from "../components/BusinessSearch/constants";

const useBusinessAtAGlance = (mapBounds, searchTerm) => {
  const [state, setState] = useState({
    loading: false,
    error: null,
    searchResults: [],
    allBusinesses: {},
    fetchedAreas: []
  });
  
  const abortControllerRef = useRef(null);

  const db = getFirestore();

  const hasAreaBeenFetched = useCallback((bounds) => {
    return state.fetchedAreas.some(area => 
      bounds.ne.lat <= area.ne.lat &&
      bounds.sw.lat >= area.sw.lat &&
      bounds.ne.lng <= area.ne.lng &&
      bounds.sw.lng >= area.sw.lng
    );
  }, [state.fetchedAreas]);

  const expandBounds = useCallback((bounds) => {
    if (!bounds) return null;
    const { BOUNDS_EXPANSION_FACTOR } = MAP_CONFIG;
    const latDiff = bounds.ne.lat - bounds.sw.lat;
    const lngDiff = bounds.ne.lng - bounds.sw.lng;
    return {
      ne: {
        lat: bounds.ne.lat + latDiff * BOUNDS_EXPANSION_FACTOR,
        lng: bounds.ne.lng + lngDiff * BOUNDS_EXPANSION_FACTOR,
      },
      sw: {
        lat: bounds.sw.lat - latDiff * BOUNDS_EXPANSION_FACTOR,
        lng: bounds.sw.lng - lngDiff * BOUNDS_EXPANSION_FACTOR,
      },
    };
  }, []);

// Update the searchBusinesses function to properly handle article tag searches
const searchBusinesses = useCallback(async (term) => {
  if (!term?.trim() || term.length < SEARCH_CONFIG.MIN_SEARCH_LENGTH) {
    setState(prev => ({ ...prev, searchResults: [] }));
    return [];
  }

  try {
    setState(prev => ({ ...prev, loading: true }));
    const searchTerm = term.toLowerCase();

    // First, search for articles with matching tags
    const articlesQuery = query(
      collection(db, "articles"),
      where("tags", "array-contains", searchTerm),
      limit(SEARCH_CONFIG.MAX_RESULTS)
    );

    const [businessSnapshot, articlesSnapshot] = await Promise.all([
      getDocs(query(collection(db, "businessAtAGlance"), orderBy("shopName"))),
      getDocs(articlesQuery)
    ]);

    // Process business results
    const businessResults = businessSnapshot.docs
      .filter(doc => {
        const data = doc.data();
        return data.shopName?.toLowerCase().includes(searchTerm) || 
               data.contactEmail?.toLowerCase().includes(searchTerm);
      })
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'business'
      }))
      .slice(0, SEARCH_CONFIG.MAX_RESULTS);

    // Process article results and get unique business IDs
    const businessIds = new Set();
    const articleResults = [];
    
    for (const doc of articlesSnapshot.docs) {
      const article = doc.data();
      if (article.authorId) {
        businessIds.add(article.authorId);
      }
    }

    // Fetch full business details for businesses found via article tags
    let businessesFromArticles = [];
    if (businessIds.size > 0) {
      const businessDetailsQuery = query(
        collection(db, "businessAtAGlance"),
        where("__name__", "in", Array.from(businessIds))
      );
      const businessDetailsSnapshot = await getDocs(businessDetailsQuery);
      businessesFromArticles = businessDetailsSnapshot.docs
        .map(doc => {
          const businessData = doc.data();
            // Find all articles for this business, separating matching and non-matching ones
            const allArticles = [];
            const matchingArticles = [];
            
            articlesSnapshot.docs.forEach(articleDoc => {
              const articleData = articleDoc.data();
              if (articleData.authorId === doc.id) {
                const article = { id: articleDoc.id, ...articleData };
                allArticles.push(article);
                if (article.tags?.includes(searchTerm)) {
                  matchingArticles.push(article);
                }
              }
            });
            
            // Sort articles: matching ones first, then by date
            const sortedArticles = [
              ...matchingArticles,
              ...allArticles.filter(article => !matchingArticles.some(m => m.id === article.id))
            ].sort((a, b) => {
              const dateA = (a.updatedAt || a.createdAt)?.seconds || 0;
              const dateB = (b.updatedAt || b.createdAt)?.seconds || 0;
              return dateB - dateA; // Newest first
            });
            
            return {
              id: doc.id,
              ...businessData,
              type: 'business',
              matchedFromArticle: matchingArticles.length > 0,
              allArticles: sortedArticles,
              matchingArticles,
              searchMatch: matchingArticles.length > 0 
                ? `Found in ${matchingArticles.length} article(s) with tag: "${searchTerm}"`
                : ''
            };
        });
    }

    // Combine results, prioritizing businesses with matching articles
    const combinedResults = [
      ...businessesFromArticles,
      ...businessResults.filter(biz => !businessesFromArticles.some(b => b.id === biz.id))
    ];

    setState(prev => ({
      ...prev,
      searchResults: combinedResults,
      loading: false,
      error: null
    }));

    return combinedResults;
  } catch (err) {
    console.error("Search error:", err);
    setState(prev => ({
      ...prev,
      error: "Failed to perform search",
      loading: false
    }));
    return [];
  }
}, [db]);

  const searchBusinessesByBounds = useCallback(async (bounds) => {
    if (!bounds || hasAreaBeenFetched(bounds)) return;

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      const q = query(
        collection(db, "businessAtAGlance"),
        where("markerPosition.lat", ">=", bounds.sw.lat),
        where("markerPosition.lat", "<=", bounds.ne.lat),
        where("markerPosition.lng", ">=", bounds.sw.lng),
        where("markerPosition.lng", "<=", bounds.ne.lng),
        limit(SEARCH_CONFIG.BOUNDS_MAX_RESULTS)
      );

      // Add a small delay to prevent rapid successive requests
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if request was aborted during delay
      if (controller.signal.aborted) return;
      
      const snapshot = await getDocs(q);
      
      // Check if request was aborted during fetch
      if (controller.signal.aborted) return;

      const newBusinesses = {};
      snapshot.docs.forEach(doc => {
        newBusinesses[doc.id] = doc.data();
      });

      setState(prev => ({
        ...prev,
        allBusinesses: { ...prev.allBusinesses, ...newBusinesses },
        fetchedAreas: [...prev.fetchedAreas, expandBounds(bounds)],
        loading: false
      }));
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }
      console.error("Bounds search error:", err);
      setState(prev => ({
        ...prev,
        error: "Failed to load businesses in this area",
        loading: false
      }));
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [db, expandBounds, hasAreaBeenFetched]);

  // Effects
  useEffect(() => {
    if (!searchTerm) return;
    const timer = setTimeout(() => searchBusinesses(searchTerm), DEBOUNCE_DELAY);
    return () => clearTimeout(timer);
  }, [searchTerm, searchBusinesses]);

  // Debounce map bounds changes
  useEffect(() => {
    if (!mapBounds) return;
    
    const timer = setTimeout(() => {
      searchBusinessesByBounds(mapBounds);
    }, 300); // 300ms debounce time
    
    return () => {
      clearTimeout(timer);
    };
  }, [mapBounds, searchBusinessesByBounds]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const businesses = useMemo(() => {
    if (searchTerm && state.searchResults.length > 0) {
      return state.searchResults;
    }
    return Object.values(state.allBusinesses).filter(business => {
      const pos = business.markerPosition;
      return pos && mapBounds &&
        pos.lat >= mapBounds.sw.lat &&
        pos.lat <= mapBounds.ne.lat &&
        pos.lng >= mapBounds.sw.lng &&
        pos.lng <= mapBounds.ne.lng;
    });
  }, [searchTerm, state.searchResults, state.allBusinesses, mapBounds]);

  return {
    businesses,
    loading: state.loading,
    error: state.error,
    searchBusinesses,
    searchBusinessesByBounds
  };
};

export default useBusinessAtAGlance;
